/**
=========================================================
* Soft UI Dashboard React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import { useState, useEffect, memo, useMemo } from "react";
import PropTypes from "prop-types";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiBadge from "components/SuiBadge";
import SuiTypography from "components/SuiTypography";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Soft UI Dashboard React context
import { useSoftUIController } from "context";

function SidenavCollapse({
  color,
  icon,
  name,
  data,
  children,
  active,
  noCollapse,
  open,
  close,
  custom,
  hasChildren,
  level = 0,
  isParentOfActive = false,
  ...rest
}) {
  const [controller] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller.theme;
  const [iconSrc, setIconSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load icon only once when component mounts
  useEffect(() => {
    let isMounted = true;

    const loadIcon = async () => {
      if (!icon) {
        setIsLoading(false);
        return;
      }

      // Handle direct URLs or static paths
      if (typeof icon === "string" && (icon.startsWith("http") || icon.startsWith("/"))) {
        if (isMounted) {
          setIconSrc(icon);
          setIsLoading(false);
        }
        return;
      }

      try {
        const iconName = icon.endsWith(".svg") ? icon.slice(0, -4) : icon;
        const module = await import(`assets/images/icons/${iconName}.svg`);
        if (isMounted) {
          setIconSrc(module.default);
          setIsLoading(false);
        }
      } catch (error) {
        console.error(`Failed to load icon ${icon}:`, error);
        if (isMounted) {
          setIconSrc(icon);
          setIsLoading(false);
        }
      }
    };

    loadIcon();

    return () => {
      isMounted = false;
    };
  }, [icon]);

  const MemoizedIcon = useMemo(() => {
    if (isLoading) {
      return <div className="icon-loading" style={{ width: "14px", height: "14.5px" }} />;
    }
    if (!iconSrc) return null;

    return (
      <img
        src={iconSrc}
        alt={name.concat("-icon")}
        className={active || isParentOfActive ? "active-icon" : ""}
        style={{
          // Bỏ transition để tránh hiệu ứng ẩn/hiện khi scroll
          width: "14px",
          height: "14.5px",
          position: "relative", // Đảm bảo icon không bị ảnh hưởng bởi scroll
          zIndex: 1, // Đặt zIndex để icon luôn hiển thị
        }}
      />
    );
  }, [isLoading, iconSrc, active, isParentOfActive, name]);

  // Memoize the entire list item content
  const listItemContent = useMemo(
    () => (
      <SuiBox
        margin="0 !important"
        {...rest}
        sx={(theme) => ({
          ...collapseItem(theme, { active, transparentSidenav }),
          paddingLeft: level > 0 ? `${level * 16}px` : "4px",
          paddingRight: "4px",
          fontWeight: active || isParentOfActive ? "700" : "500", // Đồng bộ font-weight với collapseText
          backgroundColor: "transparent",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "40px",
        })}
        className={close ? "close" : ""}
      >
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <ListItemIcon
            sx={(theme) => ({
              ...collapseIconBox(theme, { active, transparentSidenav, color }),
              marginLeft: level > 0 ? `${level * 19}px` : "0px",
              position: "relative",
              zIndex: 1,
            })}
          >
            <div
              className={
                custom
                  ? active || isParentOfActive
                    ? "sidenav-icon-custom centered"
                    : "sidenav-icon-custom-inactive centered"
                  : "sidenav-icon"
              }
              style={{
                position: "relative",
                zIndex: 1,
              }}
            >
              {MemoizedIcon}
            </div>
          </ListItemIcon>
          <ListItemText
            primary={name}
            primaryTypographyProps={{
              fontWeight: active || isParentOfActive ? "700" : "500", // Đồng bộ font-weight
              fontSize: "14px",
            }}
            sx={(theme) => ({
              ...collapseText(theme, { miniSidenav, transparentSidenav, active }),
              backgroundColor: "transparent",
            })}
          />
          {data !== 0 && data !== undefined && (
            <SuiBadge
              sx={{ position: "absolute", right: "20px", zIndex: 1 }}
              color="006CB5"
              size="sm"
              badgeContent={
                <SuiTypography variant="text" fontSize="12px" fontWeight="regular" color="#FFFFFF">
                  {data > 99 ? "99+" : data}
                </SuiTypography>
              }
            />
          )}
        </div>
        {children}
      </SuiBox>
    ),
    [
      active,
      isParentOfActive,
      close,
      level,
      name,
      data,
      MemoizedIcon,
      custom,
      color,
      miniSidenav,
      transparentSidenav,
      children,
    ]
  );

  return (
    <>
      <ListItem
        component="li"
        sx={{
          marginBottom: !hasChildren ? "8px" : "0",
          "&:hover": {
            backgroundColor: !hasChildren ? "#0094FF !important" : "#0094FF !important",
            "& .MuiTypography-root": {
              color: !hasChildren ? "#3c7363 !important" : "#3c7363 !important",
            },
            "& .submenu-arrow": {
              color: !hasChildren ? "#3c7363 !important" : "#3c7363 !important",
            },
            "& img": { filter: !hasChildren ? "none" : "none" },
            borderLeft: !hasChildren ? "2px solid white" : "none",
          },
          backgroundColor: active && !close ? "#0094FF !important" : "transparent",
          color: active ? "#3c7363 !important" : "inherit",
          borderLeft: active && !hasChildren ? "2px solid white" : "none",
          width: "100%",
          padding: "0",
          height: "40px",
          boxSizing: "border-box",
        }}
      >
        {listItemContent}
      </ListItem>
      {children && (
        <Collapse in={open} unmountOnExit sx={{ marginBottom: "8px" }}>
          {children}
        </Collapse>
      )}
    </>
  );
}

// Update propTypes
SidenavCollapse.defaultProps = {
  color: "info",
  active: false,
  noCollapse: false,
  children: false,
  open: false,
  data: 0,
  close: false,
  custom: false,
  hasChildren: false,
  level: 0,
  isParentOfActive: false,
};

SidenavCollapse.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  name: PropTypes.string.isRequired,
  data: PropTypes.number,
  children: PropTypes.node,
  active: PropTypes.bool,
  noCollapse: PropTypes.bool,
  open: PropTypes.bool,
  close: PropTypes.bool,
  custom: PropTypes.bool,
  hasChildren: PropTypes.bool,
  level: PropTypes.number,
  isParentOfActive: PropTypes.bool,
};

export default memo(SidenavCollapse);
